import { useEffect, useRef, useState, useCallback } from "react";
import * as echarts from "echarts";
import type { EChartsOption, ECharts } from "echarts";
import { EXCHANGES, EXCHANGE_COLORS } from "@/constants/coinglass/coinglass";
import type {
  FundingRateDataPoint,
  ZoomState,
  DataZoomEvent,
  TimeRange,
} from "@/types/coinglass";
import {
  capitalizeExchange,
  aggregateData,
  getAggregationLevel,
} from "@/utils/coinglass";
import { formatDateLabel } from "@/utils/charts/dataUtils";
import { useTokenPriceData } from "@/hooks/coinglass/useTokenPriceData";
import { useFundingRateData } from "@/hooks/coinglass/useFundingRateData";
import TimeRangePicker from "@/components/common/TimeRangePicker";

interface TokenFundingRateChartProps {
  ticker?: string;
  className?: string;
  hideHeader?: boolean;
  hideTimeRange?: boolean;
  externalRange?: TimeRange;
  containerClassName?: string;
}

function TokenFundingRateChart({
  ticker = "BTC",
  className = "",
  hideHeader = false,
  hideTimeRange = false,
  externalRange,
  containerClassName,
}: TokenFundingRateChartProps) {
  const chartRef = useRef<HTMLDivElement>(null);
  const chartInstanceRef = useRef<ECharts | null>(null);
  const rawDataRef = useRef<FundingRateDataPoint[]>([]);
  const isUpdatingRef = useRef(false);
  const lastZoomRef = useRef<ZoomState>({ start: 0, end: 100, aggregation: 1 });

  const [activeRange, setActiveRange] = useState<TimeRange>("3M");
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [priceWarning, setPriceWarning] = useState<boolean>(false);

  const priceQuery = useTokenPriceData(ticker);
  const fundingRateQuery = useFundingRateData(ticker, [...EXCHANGES]);

  useEffect(() => {
    if (fundingRateQuery.isLoading || priceQuery.isLoading) {
      setLoading(true);
      return;
    }
    if (fundingRateQuery.isError) {
      setError(
        fundingRateQuery.error instanceof Error
          ? fundingRateQuery.error.message
          : "Failed to fetch funding rate data",
      );
      setLoading(false);
      return;
    }
    if (!fundingRateQuery.data || fundingRateQuery.data.length === 0) {
      setError(`No funding rate data available for ${ticker}`);
      setLoading(false);
      return;
    }
    if (
      priceQuery.isError ||
      !priceQuery.data ||
      priceQuery.data.length === 0
    ) {
      setPriceWarning(true);
    } else {
      setPriceWarning(false);
    }

    const fundingData = fundingRateQuery.data;
    const priceData = priceQuery.data || [];
    const priceMap = new Map<number, number | null>();
    priceData.forEach((p) => {
      priceMap.set(p.timestamp, p.price);
    });

    const dataPoints: FundingRateDataPoint[] = fundingData.map((point) => ({
      ...point,
      price: priceMap.get(point.timestamp) ?? null,
    }));

    rawDataRef.current = dataPoints;
    setLoading(false);
    setError(null);
  }, [
    ticker,
    fundingRateQuery.isLoading,
    fundingRateQuery.isError,
    fundingRateQuery.error,
    fundingRateQuery.data,
    priceQuery.isLoading,
    priceQuery.isError,
    priceQuery.data,
  ]);

  const calculateZoomStart = useCallback((range: TimeRange): number => {
    const totalPoints = rawDataRef.current.length;
    switch (range) {
      case "1D":
        return Math.max(0, 100 - (1 / totalPoints) * 100);
      case "7D":
        return Math.max(0, 100 - (7 / totalPoints) * 100);
      case "1M":
        return Math.max(0, 100 - (30 / totalPoints) * 100);
      case "3M":
        return Math.max(0, 100 - (90 / totalPoints) * 100);
      case "ALL":
      default:
        return 0;
    }
  }, []);

  const handleTimeRangeClick = useCallback(
    (range: TimeRange) => {
      setActiveRange(range);
      if (chartInstanceRef.current) {
        const start = calculateZoomStart(range);
        isUpdatingRef.current = true;
        chartInstanceRef.current.dispatchAction({
          type: "dataZoom",
          start,
          end: 100,
        });
        setTimeout(() => {
          isUpdatingRef.current = false;
        }, 100);
      }
    },
    [calculateZoomStart],
  );

  useEffect(() => {
    if (!externalRange) return;
    handleTimeRangeClick(externalRange);
  }, [externalRange, handleTimeRangeClick]);

  useEffect(() => {
    if (
      loading ||
      error ||
      !chartRef.current ||
      rawDataRef.current.length === 0
    ) {
      return;
    }

    const chartInstance = echarts.init(chartRef.current, "dark");
    chartInstanceRef.current = chartInstance;

    const initialStart = calculateZoomStart("3M");
    const totalPoints = rawDataRef.current.length;
    const initialVisiblePercentage = 100 - initialStart;
    const initialAggregation = getAggregationLevel(
      totalPoints,
      initialVisiblePercentage,
    );

    const displayData = aggregateData(rawDataRef.current, initialAggregation);

    const timestamps = displayData.map((d) => {
      try {
        if (!d?.timestamp) return "";
        return formatDateLabel(d.timestamp, "medium");
      } catch {
        return d?.timestamp ? String(d.timestamp) : "";
      }
    });

    const prices = displayData.map((d) => d.price);
    const avgFundingRates = displayData.map((d) => {
      const rates = Object.values(d.exchanges);
      if (rates.length === 0) return 0;
      return rates.reduce((acc, rate) => acc + rate, 0) / rates.length;
    });

    const option: EChartsOption = {
      backgroundColor: "transparent",
      grid: {
        left: "3%",
        right: "3%",
        top: "15%",
        bottom: "15%",
        containLabel: true,
      },
      tooltip: {
        trigger: "axis",
        axisPointer: { type: "cross", crossStyle: { color: "#999" } },
        backgroundColor: "rgba(17, 24, 39, 0.95)",
        borderColor: "#374151",
        borderWidth: 1,
        textStyle: { color: "#f3f4f6" },
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        formatter: (params: any) => {
          if (!Array.isArray(params) || params.length === 0) return "";
          const dataIndex = params[0].dataIndex as number;
          const dataPoint = displayData[dataIndex];
          if (!dataPoint) return "";
          const dateStr = formatDateLabel(dataPoint.timestamp, "medium");
          let tooltipHtml = `<div style="padding: 8px;">`;
          tooltipHtml += `<div style="font-size:11px;font-weight:500;margin-bottom:8px;color:#9ca3af;">${dateStr}</div>`;
          tooltipHtml += `<div style="margin-bottom: 8px;">`;
          tooltipHtml += `<span style="color: #d1d5db;">● ${ticker} Price</span>`;
          if (dataPoint.price !== null) {
            tooltipHtml += `<span style="float: right; margin-left: 20px; font-weight: 600; color: #f3f4f6;">$${dataPoint.price.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>`;
          } else {
            tooltipHtml += `<span style="float: right; margin-left: 20px; color: #6B7280;">N/A</span>`;
          }
          tooltipHtml += `</div>`;
          tooltipHtml += `<div style="border-top: 1px solid #374151; padding-top: 8px; margin-top: 4px;">`;
          Object.entries(dataPoint.exchanges)
            .sort((a, b) => a[0].localeCompare(b[0]))
            .forEach(([exchange, rate]) => {
              const ratePercent = rate.toFixed(4);
              const color = EXCHANGE_COLORS[exchange] ?? "#9CA3AF";
              const displayName = capitalizeExchange(exchange);
              const rateColor =
                rate > 0 ? "#10B981" : rate < 0 ? "#EF4444" : "#9CA3AF";
              tooltipHtml += `<div style="margin-bottom: 4px; display: flex; justify-content: space-between; align-items: center;">`;
              tooltipHtml += `<span style="color: ${color};">● ${displayName}</span>`;
              tooltipHtml += `<span style="margin-left: 20px; font-weight: 600; color: ${rateColor};">${ratePercent}%</span>`;
              tooltipHtml += `</div>`;
            });
          tooltipHtml += `</div>`;
          tooltipHtml += `</div>`;
          return tooltipHtml;
        },
      },
      xAxis: {
        type: "category",
        data: timestamps,
        axisLine: { lineStyle: { color: "#4b5563" } },
        axisLabel: { color: "#9ca3af", margin: 14 },
        splitLine: { show: false },
      },
      yAxis: [
        {
          type: "value",
          name: "Funding Rate",
          position: "left",
          axisLine: { lineStyle: { color: "#4b5563" } },
          axisLabel: {
            color: "#9ca3af",
            formatter: (v: number | string) =>
              `${(typeof v === "string" ? parseFloat(v) : v).toFixed(3)}%`,
          },
          splitLine: { lineStyle: { color: "#374151" } },
        },
        {
          type: "value",
          name: "Price",
          position: "right",
          axisLine: { lineStyle: { color: "#4b5563" } },
          axisLabel: {
            color: "#9ca3af",
            formatter: (v: number | string) =>
              `$${((typeof v === "string" ? parseFloat(v) : v) / 1000).toFixed(0)}K`,
          },
          splitLine: { show: false },
        },
      ],
      dataZoom: [
        {
          type: "slider",
          start: initialStart,
          end: 100,
          height: 30,
          bottom: 10,
        },
        { type: "inside", start: initialStart, end: 100 },
      ],
      series: [
        {
          name: "Avg Funding Rate",
          type: "bar",
          yAxisIndex: 0,
          data: avgFundingRates,
          itemStyle: {
            color: (params) => {
              const value = params.value as number;
              return value >= 0
                ? "rgba(16, 185, 129, 0.7)"
                : "rgba(239, 68, 68, 0.7)";
            },
          },
          emphasis: {
            itemStyle: {
              color: (params) => {
                const value = params.value as number;
                return value >= 0
                  ? "rgba(16, 185, 129, 0.9)"
                  : "rgba(239, 68, 68, 0.9)";
              },
            },
          },
        },
        {
          name: `${ticker} Price`,
          type: "line",
          yAxisIndex: 1,
          data: prices,
          smooth: true,
          symbol: "none",
          lineStyle: { color: "#fbbf24", width: 2 },
          itemStyle: { color: "#fbbf24" },
        },
      ],
    };

    chartInstance.setOption(option);
    lastZoomRef.current.aggregation = initialAggregation;

    let debounceTimer: ReturnType<typeof setTimeout> | null = null;
    const handleDataZoom = (event: unknown) => {
      if (isUpdatingRef.current) return;
      const zoomEvent = event as DataZoomEvent;
      const start = zoomEvent.start ?? zoomEvent.batch?.[0]?.start ?? 0;
      const end = zoomEvent.end ?? zoomEvent.batch?.[0]?.end ?? 100;
      if (
        start === lastZoomRef.current.start &&
        end === lastZoomRef.current.end
      )
        return;
      lastZoomRef.current = {
        start,
        end,
        aggregation: lastZoomRef.current.aggregation,
      };
      if (debounceTimer) clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => {
        const visiblePercentage = end - start;
        const newAggregation = getAggregationLevel(
          rawDataRef.current.length,
          visiblePercentage,
        );
        if (newAggregation === lastZoomRef.current.aggregation) return;
        lastZoomRef.current.aggregation = newAggregation;
        const newAggregatedData = aggregateData(
          rawDataRef.current,
          newAggregation,
        );
        const newTimestamps = newAggregatedData.map((d) =>
          formatDateLabel(d.timestamp, "medium"),
        );
        const newPrices = newAggregatedData.map((d) => d.price);
        const newAvgRates = newAggregatedData.map((d) => {
          const rates = Object.values(d.exchanges);
          if (rates.length === 0) return 0;
          return rates.reduce((acc, rate) => acc + rate, 0) / rates.length;
        });
        isUpdatingRef.current = true;
        chartInstance.setOption(
          {
            xAxis: { data: newTimestamps },
            series: [{ data: newAvgRates }, { data: newPrices }],
          },
          { notMerge: false },
        );
        setTimeout(() => {
          isUpdatingRef.current = false;
        }, 100);
      }, 250);
    };
    chartInstance.on("dataZoom", handleDataZoom);
    const handleResize = () => chartInstance.resize();
    window.addEventListener("resize", handleResize);
    return () => {
      if (debounceTimer) clearTimeout(debounceTimer);
      window.removeEventListener("resize", handleResize);
      chartInstance.off("dataZoom", handleDataZoom);
      chartInstance.dispose();
    };
  }, [ticker, loading, error, priceWarning, calculateZoomStart]);

  if (loading) {
    return (
      <div className={`w-full ${className}`}>
        {/* {!hideHeader && (
          <h2 className="mb-4 text-2xl font-bold text-white">
            {ticker} Funding Rate History Chart
          </h2>
        )} */}
        <div className="flex h-[500px] w-full items-center justify-center rounded-lg bg-gray-900 p-4">
          <div className="text-center">
            <div className="mb-4 h-12 w-12 animate-spin rounded-full border-4 border-gray-700 border-t-blue-500 mx-auto"></div>
            <p className="text-gray-400">Loading funding rate data...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`w-full ${className}`}>
        {/* {!hideHeader && (
          <h2 className="mb-4 text-2xl font-bold text-white">
            {ticker} Funding Rate History Chart
          </h2>
        )} */}
        <div className="flex h-[500px] w-full items-center justify-center rounded-lg bg-gray-900 p-4">
          <div className="text-center max-w-md mx-auto">
            <div className="mb-4 text-6xl">⚠️</div>
            <p className="text-red-400 font-semibold mb-2 text-lg">
              Failed to Load Data
            </p>
            <p className="text-gray-400 text-sm mb-4">{error}</p>
            <p className="text-gray-500 text-xs border border-gray-700 rounded-lg p-3 bg-gray-800">
              Please try again. If the issue persists, contact support.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`w-full ${className}`}>
      {!hideHeader && (
        <div className="mb-4 flex items-center justify-end">
          {/* <h2 className="text-2xl font-bold text-white">
            {ticker} Funding Rate History Chart
          </h2> */}
          {!hideTimeRange && (
            <TimeRangePicker
              options={["1D", "7D", "1M", "3M", "ALL"] as const}
              value={activeRange}
              onChange={(v) => handleTimeRangeClick(v as TimeRange)}
              theme="blue"
            />
          )}
        </div>
      )}
      <div
        ref={chartRef}
        className={
          containerClassName ?? "h-[500px] w-full rounded-lg bg-gray-900 p-4"
        }
      />
    </div>
  );
}

export default TokenFundingRateChart;
